import React, { useEffect, useState } from "react";

/**
 * Page-pill pagination matching the design reference.
 * Renders prev/next arrows + a windowed page list with ellipsis for long lists.
 *
 * Props:
 *   currentPage - active page (1-based)
 *   totalPages  - total number of pages
 *   onPageChange(page) - called when the user picks a page
 *   totalItems? - optional, shown to the left for context
 */
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  totalItems,
}) => {
  const cp = Math.max(1, Number(currentPage) || 1);
  const tp = Math.max(1, Number(totalPages) || 1);

  // Show a spinner on the page the user just clicked, until the parent's
  // currentPage catches up. Lets the user see *something* happen while the
  // list refetches.
  const [pendingPage, setPendingPage] = useState(null);

  // Once currentPage matches the requested page, we're done. Also use a
  // 10s safety timeout so a failed request can't lock the controls forever.
  useEffect(() => {
    if (pendingPage !== null && cp === pendingPage) {
      setPendingPage(null);
    }
  }, [cp, pendingPage]);

  useEffect(() => {
    if (pendingPage === null) return undefined;
    const timer = setTimeout(() => setPendingPage(null), 10000);
    return () => clearTimeout(timer);
  }, [pendingPage]);

  const isLoading = pendingPage !== null;

  const go = (page) => {
    if (isLoading) return;
    if (page < 1 || page > tp || page === cp) return;
    setPendingPage(page);
    if (typeof onPageChange === "function") onPageChange(page);
  };

  // Build a compact list: 1 … (cp-1) cp (cp+1) … tp
  const pages = [];
  const add = (p) => {
    if (!pages.includes(p) && p >= 1 && p <= tp) pages.push(p);
  };
  add(1);
  for (let p = cp - 1; p <= cp + 1; p += 1) add(p);
  add(tp);
  pages.sort((a, b) => a - b);

  // Insert ellipsis sentinels (string) between non-consecutive numbers.
  const rendered = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) rendered.push("…");
    rendered.push(p);
  });

  return (
    <div className="pager">
      <div className="pager-controls">
        <button
          type="button"
          className="pager-arrow"
          disabled={cp <= 1 || isLoading}
          onClick={() => go(cp - 1)}
          aria-label="Previous page"
        >
          <i className="fas fa-chevron-left" />
        </button>
        {rendered.map((token, idx) =>
          token === "…" ? (
            <span key={`ell-${idx}`} className="pager-ellipsis">
              …
            </span>
          ) : (
            <button
              type="button"
              key={`p-${token}`}
              className={`pager-page${token === cp ? " active" : ""}${
                pendingPage === token ? " loading" : ""
              }`}
              disabled={isLoading}
              onClick={() => go(token)}
              aria-busy={pendingPage === token ? "true" : undefined}
            >
              {pendingPage === token ? (
                <i className="fas fa-spinner fa-spin" />
              ) : (
                token
              )}
            </button>
          ),
        )}
        <button
          type="button"
          className="pager-arrow"
          disabled={cp >= tp || isLoading}
          onClick={() => go(cp + 1)}
          aria-label="Next page"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
